'use client';

import { useCallback } from 'react';

type UseGrammarTextareaHandlersProps = {
  /**
   * Callback for when text should be split and a new step created
   * Returns the text that should remain in the current textarea
   */
  onCreateNewStep?: (text: string, cursorPosition: number) => string | void;
  /**
   * Callback for when an empty step should be deleted
   */
  onDeleteEmptyStep?: () => void;
  /**
   * Callback for when text should be merged with the previous step
   * Called when backspace/delete is pressed at the beginning of a step with text
   */
  onMergeWithPreviousStep?: (currentText: string) => void;
};

/**
 * Hook for handling grammar textarea-specific keyboard events
 * Provides a simplified interface that works alongside step navigation hooks
 */
function useGrammarTextareaHandlers({
  onCreateNewStep,
  onDeleteEmptyStep,
  onMergeWithPreviousStep,
}: UseGrammarTextareaHandlersProps) {
  /**
   * Handle keydown events in the textarea
   */
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    const textarea = e.currentTarget;
    const { value, selectionStart, selectionEnd } = textarea;

    // Handle Enter key press without shift key - create a new step
    if (e.key === 'Enter' && !e.shiftKey && onCreateNewStep) {
      e.preventDefault();
      const updatedText = onCreateNewStep(value, selectionStart);
      // If the callback returns a string, update the textarea
      if (typeof updatedText === 'string') {
        textarea.value = updatedText;
        const event = new Event('input', { bubbles: true });
        textarea.dispatchEvent(event);
      }
    }

    // Handle Backspace or Delete key when textarea is empty - delete the step
    if ((e.key === 'Backspace' || e.key === 'Delete') && value === '' && onDeleteEmptyStep) {
      e.preventDefault();
      onDeleteEmptyStep();
    }

    // Handle Backspace at the beginning of a step with text - merge with previous step
    // Only trigger if cursor is at the beginning (position 0) with no text selection
    if (e.key === 'Backspace' && selectionStart === 0 && selectionEnd === 0 && value !== '' && onMergeWithPreviousStep) {
      e.preventDefault();
      onMergeWithPreviousStep(value);
    }
  }, [onCreateNewStep, onDeleteEmptyStep, onMergeWithPreviousStep]);

  /**
   * Handle paste events in the textarea
   */
  const handlePaste = useCallback((e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    // Only proceed if we have a callback for creating new steps and there are newlines
    if (!onCreateNewStep || !e.clipboardData.getData('text').includes('\n'))
      return;

    e.preventDefault();

    const textarea = e.currentTarget;
    const pastedText = e.clipboardData.getData('text');
    const { value, selectionStart, selectionEnd } = textarea;

    // Process the first line
    const lines = pastedText.split('\n');
    const firstLine = lines[0] || '';
    const textBeforeCursor = value.substring(0, selectionStart);
    const textAfterCursor = value.substring(selectionEnd);

    // Update the current step with the first line
    const newValue = textBeforeCursor + firstLine + textAfterCursor;
    textarea.value = newValue;
    // Trigger React's onChange
    const inputEvent = new InputEvent('input', { bubbles: true });
    textarea.dispatchEvent(inputEvent);

    // Let the parent handle the rest of the lines
    if (lines.length > 1) {
      onCreateNewStep(lines.slice(1).join('\n'), 0);
    }
  }, [onCreateNewStep]);

  return {
    handleKeyDown,
    handlePaste,
  };
}

export default useGrammarTextareaHandlers;
